'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Award, Users, MapPin, Trophy, Star } from 'lucide-react';

const navItems = [
  { label: 'Achievers', href: '/public-profile-page', icon: Users },
  { label: 'Districts', href: '#districts', icon: MapPin },
  { label: 'Awards', href: '#awards', icon: Trophy },
  { label: 'Hall of Fame', href: '#hall-of-fame', icon: Star },
  { label: 'Admin', href: '/admin-dashboard', icon: Award },
];

export default function PublicHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-card border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 shrink-0">
            <AppLogo size={36} />
            <div className="hidden sm:block">
              <div className={`font-bold text-base leading-tight tracking-tight transition-colors ${scrolled ? 'text-primary' : 'text-white'}`}>
                Ang Pradesh
              </div>
              <div className={`text-xs font-medium tracking-wide transition-colors ${scrolled ? 'text-muted-foreground' : 'text-white/70'}`}>
                Alumni & Achievers Network
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems?.map((item) => (
              <Link
                key={`nav-${item?.label}`}
                href={item?.href}
                className={`nav-link text-sm font-medium transition-colors ${
                  scrolled ? 'text-muted-foreground hover:text-primary' : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {item?.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/nominate-an-achiever"
              className={`text-sm font-semibold px-4 py-2 rounded-lg border-2 transition-all duration-150 ${
                scrolled
                  ? 'border-primary text-primary hover:bg-primary hover:text-white' :'border-white/60 text-white hover:bg-white/10'
              }`}
            >
              Nominate
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="btn-accent text-sm px-5 py-2"
            >
              Register Profile
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-primary hover:bg-muted' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-border shadow-card-hover animate-slide-up">
          <div className="px-4 py-4 space-y-1">
            {navItems?.map((item) => (
              <Link
                key={`mobile-nav-${item?.label}`}
                href={item?.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors"
              >
                <item.icon size={16} className="text-muted-foreground" />
                {item?.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-border space-y-2">
              <Link href="/nominate-an-achiever" onClick={() => setMenuOpen(false)} className="btn-outline w-full text-sm">
                Nominate an Achiever
              </Link>
              <Link href="/sign-up-login-screen" onClick={() => setMenuOpen(false)} className="btn-accent w-full text-sm">
                Register Profile
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}